import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, WMSTileLayer, Polygon, Popup, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Trees, MapPin, Search, Loader2, ExternalLink, Ruler, Building2, Sparkles, AlertTriangle, Plus, Pencil, Trash2, Globe2, Copy, FileText, Waves, ChevronDown, ChevronRight, Tag, X, Link as LinkIcon, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import ZonaDialog from "@/components/ZonaDialog";
import PublicadoDialog from "@/components/PublicadoDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buildDireccionQuery } from "@/constants/portales";
import { tieneProteccionEspecial } from "@/lib/lote";
import { startLandHoTour, hasSeenTour, TOUR_START_EVENT } from "@/lib/tour";
import { TermHelp, TERM_GLOSSARY } from "@/components/TermHelp";

// ─────────────────────────────────────────────────────────────
// Tile providers (satélite + calles). Sin API key, verificado.
// ─────────────────────────────────────────────────────────────
const TILE_SAT = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const TILE_SAT_ATTR = "Tiles © Esri";
const TILE_STREETS = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const TILE_STREETS_ATTR = "© OpenStreetMap · © CARTO";

// ─────────────────────────────────────────────────────────────
// IDECOR WMS — capas oficiales de hidrografía y riesgo hídrico.
// Verificado en GetCapabilities 2026-02 (workspace `idecor`).
// Regla de honestidad: la cobertura de riesgo es MUNICIPAL y PARCIAL
// (hoy: Unquillo + Villa Carlos Paz). La ausencia de color NO implica
// ausencia de riesgo — se aclara en la leyenda del panel.
// ─────────────────────────────────────────────────────────────
const IDECOR_WMS_URL = "https://idecor-ws.mapascordoba.gob.ar/geoserver/idecor/wms";
const IDECOR_WMS_ATTR = "IDECOR — Mapas Córdoba";
// Riesgo de inundación agrupa TODAS las capas municipales que IDECOR
// publique (Unquillo + Carlos Paz + …). WMS soporta layers coma-separadas
// en un solo GetMap → una request en vez de tres. Si mañana IDECOR suma
// otro municipio (ej: La Falda), se agrega acá y listo.
const WMS_LAYER_CURSOS_AGUA = "cursos_agua";
const WMS_LAYER_CUENCAS = "cuencas_hidrograficas";
const WMS_LAYERS_RIESGO_INUN = [
  "unq_suba_ries_inun",              // Unquillo — áreas no urbanizables por riesgo inundación
  "Carlos_Paz_Anegamiento_Humedales", // Villa Carlos Paz — anegamiento + humedales
  "Carlos_Paz_Peligrosidad",          // Villa Carlos Paz — peligrosidad (incluye hídrica)
].join(",");

// ─────────────────────────────────────────────────────────────
// Publicado — pin naranja para avisos de terrenos en venta.
// `L.divIcon` HTML custom, sin depender de leaflet-images (evita el 404
// clásico de marker-icon.png cuando bundler no publica assets estáticos).
// Anchor centrado en el círculo (16,16). Se instancia una sola vez.
// ─────────────────────────────────────────────────────────────
const PIN_PUBLICADO = L.divIcon({
  className: "radar-pin-publicado",
  html: '<div style="width:16px;height:16px;border-radius:50%;background:#ea580c;border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -10],
});

// Formato $ para la ficha del aviso. Espeja convenciones AR: USD/ARS
// grande, "S/D" cuando falta info (Regla de Honestidad — no inventar).
const fmtMoney = (v, moneda) => {
  if (v == null || v === "") return "S/D";
  const n = Number(v);
  if (!Number.isFinite(n)) return "S/D";
  const sym = moneda === "USD" ? "USD" : moneda === "ARS" ? "$" : (moneda || "");
  return `${sym} ${n.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
};

// Frescura relativa honesta (2026-07-25). Lee ISO de `last_ok_at` y
// devuelve el string amigable. Regla de Honestidad: NUNCA redondear a
// "hoy" si la data es vieja — mostramos "hace 3 días" tal cual.
// Devuelve null si iso es null/inválido → el caller no renderiza texto.
const fmtFrescura = (iso) => {
  if (!iso) return null;
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return null;
  const diffMs = Date.now() - dt.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "hace menos de 1 min";
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `hace ${diffH} h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "ayer";
  if (diffD < 30) return `hace ${diffD} días`;
  const diffMonths = Math.floor(diffD / 30);
  if (diffMonths < 12) return `hace ${diffMonths} mes${diffMonths === 1 ? "" : "es"}`;
  return `hace ${Math.floor(diffD / 365)} año${Math.floor(diffD / 365) === 1 ? "" : "s"}`;
};

const CBA = [-31.4135, -64.1811];

// ─────────────────────────────────────────────────────────────
// Panel de filtros (dual-range simple con inputs)
// ─────────────────────────────────────────────────────────────
function RangeField({ label, value, onChange, testidPrefix, step = 1 }) {
  const [min, max] = value;
  return (
    <div className="mb-4">
      <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1.5 block">{label}</label>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number" step={step} placeholder="desde"
          value={min ?? ""}
          onChange={(e) => onChange([e.target.value ? Number(e.target.value) : null, max])}
          data-testid={`${testidPrefix}-min`}
          className="border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-emerald-700"
        />
        <input
          type="number" step={step} placeholder="hasta"
          value={max ?? ""}
          onChange={(e) => onChange([min, e.target.value ? Number(e.target.value) : null])}
          data-testid={`${testidPrefix}-max`}
          className="border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-emerald-700"
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Ficha lateral (drawer)
// ─────────────────────────────────────────────────────────────
function LoteFicha({ lote, onClose, onPublicado }) {
  if (!lote) return null;
  const c = lote.centroide?.coordinates || [null, null];
  const lat = c[1], lng = c[0];
  const gmaps = `https://www.google.com/maps?q=${lat},${lng}`;
  const streetview = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
  const fmt = (n) => (n == null ? "s/d" : Math.round(n).toLocaleString("es-AR"));
  const fmtM2 = (n) => (n == null ? "s/d" : `${Math.round(n).toLocaleString("es-AR")} m²`);
  const enlaceNormativa = (v) => (v && v.startsWith("http") ? v : v);
  // ¿Botón "¿Está publicado?" disponible?
  const publicadoInfo = buildDireccionQuery(lote);

  // Alerta de protección especial — regla centralizada en `@/lib/lote`
  // (misma detección que usa el mapa · sin drift entre vistas).
  const proteccionEspecial = tieneProteccionEspecial(lote);

  return (
    <div className="fixed inset-0 z-[900] flex justify-end" data-testid="lote-ficha">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-800">Lote catastral</p>
              <h2 className="font-heading text-xl font-bold text-slate-900 mt-1">
                {lote.desig_oficial || lote.nomenclatura}
              </h2>
              <p className="text-xs font-mono text-slate-500 mt-1">Nom: {lote.nomenclatura}</p>
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="lote-ficha-close">✕</button>
          </div>
          {lote.tocan_multiples_zonas && (
            <div className="mt-3 flex items-start gap-2 border border-amber-500 bg-amber-50 p-2 text-xs">
              <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <span className="text-amber-900">Este lote toca 2 zonas — verificar (una mitad puede tener PB+7 y otra PB+2).</span>
            </div>
          )}
          {lote.es_candidato_urbanizacion && (
            <div className="mt-3 flex items-start gap-2 border border-emerald-600 bg-emerald-50 p-2 text-xs">
              <Sparkles className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
              <span className="text-emerald-900 font-medium">Zona en proceso de urbanización — señal ⭐</span>
            </div>
          )}
        </div>

        <div className="p-6 space-y-6">
          <section>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600 mb-2">Datos catastrales</p>
            <dl className="text-sm space-y-1.5">
              <div className="flex justify-between"><dt className="text-slate-500">Superficie</dt><dd className="font-mono font-semibold">{fmtM2(lote.superficie_m2)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Valuación oficial<TermHelp text={TERM_GLOSSARY.valuacion} label="Valuación oficial" testid="help-valuacion" /></dt><dd className="font-mono">ARS {fmt(lote.valuacion_ars)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">ARS/m²<TermHelp text={TERM_GLOSSARY.ars_m2} label="ARS por m²" testid="help-ars-m2" /></dt><dd className="font-mono">{fmt(lote.valuacion_ars_m2)}</dd></div>
              <div className="flex justify-between items-center gap-2">
                <dt className="text-slate-500">Nro. Cuenta</dt>
                {lote.nro_cuenta ? (
                  <dd className="font-mono flex items-center gap-1.5">
                    <span>{lote.nro_cuenta}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(lote.nro_cuenta).then(
                          () => toast.success("Nº de cuenta copiado"),
                          () => toast.error("No pude copiar — copialo a mano"),
                        );
                      }}
                      title="Copiar al portapapeles"
                      data-testid="btn-copiar-cuenta"
                      className="text-slate-400 hover:text-emerald-700 transition-colors p-0.5"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </dd>
                ) : (
                  <dd className="font-mono text-slate-400">s/d</dd>
                )}
              </div>
              <div className="flex justify-between"><dt className="text-slate-500">Categoría uso suelo<TermHelp text={TERM_GLOSSARY.categoria_uso} label="Categoría uso suelo" testid="help-categoria-uso" /></dt><dd>{lote.categoria_uso_suelo || "s/d"}</dd></div>
            </dl>
          </section>

          <section>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600 mb-2">Ubicación</p>
            <dl className="text-sm space-y-1.5">
              <div><dt className="text-slate-500 text-xs">Dirección</dt><dd className="mt-0.5">{lote.direccion || <span className="italic text-slate-400 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />Obteniendo dirección…</span>}</dd></div>
              <div><dt className="text-slate-500 text-xs">Barrio</dt><dd className="mt-0.5">{lote.barrio || (lote.direccion ? "s/d" : <span className="italic text-slate-400">—</span>)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Localidad</dt><dd>{lote.localidad || "s/d"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Departamento</dt><dd>{lote.departamento || "s/d"}</dd></div>
            </dl>
          </section>

          <section className="border-t-2 border-emerald-700 pt-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-800 mb-2">Qué se puede construir</p>
            {/* Header fuente normativa */}
            {lote.normativa_ord ? (
              <div className="mb-3 text-xs">
                <span className="font-bold text-slate-700">{lote.normativa_localidad}</span>
                <span className="text-slate-500"> — {lote.normativa_ord}</span>
                {lote.normativa_zona && (
                  <span className="text-slate-500"> · Zona {lote.normativa_zona}</span>
                )}
                {lote.normativa_denominacion && (
                  <p className="text-[11px] text-slate-500 mt-0.5 italic">{lote.normativa_denominacion}</p>
                )}
              </div>
            ) : (
              <div className="mb-3 text-xs bg-slate-100 border-l-2 border-slate-400 p-2">
                <span className="font-bold text-slate-700">Normativa no disponible para este municipio.</span>
                <p className="text-[11px] text-slate-600 mt-0.5">
                  El lote catastral existe pero la ordenanza local no está publicada en IDECOR.
                  Verificá directamente con el municipio.
                </p>
              </div>
            )}

            {/* Alerta protección especial (Fase 2 · 2026-07-17).
                Dispara cuando `caracter_urbanistico` contiene
                PRESERVACIÓN / HISTÓRIC / PATRIMONIAL / PAISAJÍSTIC.
                Copy honesto — no afirma "no se puede demoler", solo que
                hay condición especial a verificar (varía por patrón y
                municipio). Muestra el fragmento textual del catastro
                para que el desarrollador vea EXACTAMENTE qué disparó. */}
            {proteccionEspecial && (
              <div
                className="mb-3 border-l-4 border-red-600 bg-red-50 p-3"
                data-testid="banner-proteccion-especial"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-bold text-red-900 leading-snug">
                      Zona con protección o condición especial (patrimonial, histórica, paisajística o ambiental).
                    </p>
                    <p className="text-red-800 mt-1 leading-snug">
                      Puede tener restricciones para demoler o desarrollar. Verificá en la ordenanza antes de avanzar.
                    </p>
                    <p className="text-[11px] text-red-900/80 mt-2 italic border-t border-red-200 pt-1.5 whitespace-pre-line">
                      {lote.caracter_urbanistico}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Banner fórmula condicional */}
            {lote.es_formula_condicional && (
              <div className="mb-3 text-xs bg-amber-50 border border-amber-400 p-2" data-testid="banner-formula">
                <p className="font-bold text-amber-900">⚠️ El FOS/FOT varía según pendiente del terreno.</p>
                <p className="text-amber-800 mt-0.5">
                  No se puede calcular m² construibles automáticamente — requiere análisis manual con relevamiento topográfico.
                </p>
              </div>
            )}

            {/* Núcleo: m² construibles + unidades máximas (REGLA DE ORO) */}
            {(lote.m2_construibles || lote.unidades_maximas) ? (
              <div className={`p-3 mb-3 border-2 ${lote.advertencia_sin_unidades ? "border-amber-400 bg-amber-50" : "border-emerald-700 bg-emerald-50"}`}
                   data-testid="potencial-box">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 inline-flex items-center">m² construibles<TermHelp text={TERM_GLOSSARY.m2_constr} label="m² construibles" testid="help-m2-construibles" /></p>
                    <p className={`font-mono font-bold text-2xl ${lote.advertencia_sin_unidades ? "text-amber-800" : "text-emerald-900"}`}
                       data-testid="m2-construibles">
                      {fmtM2(lote.m2_construibles)}
                    </p>
                    {/* Nota chica: cuándo el tope FOT pisó al cálculo por
                        pisos (fix Fase 2 addendum). El número honesto es
                        el conservador, y explicamos por qué no es más alto. */}
                    {lote.m2_construibles_limitado_por_fot && (
                      <p className="text-[10px] text-slate-500 mt-0.5" data-testid="m2-limitado-fot">
                        limitado por el FOT (Ordenanza)
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Unidades máx.</p>
                    <p className={`font-mono font-bold text-2xl ${lote.unidades_maximas ? "text-emerald-900" : "text-slate-400"}`}
                       data-testid="unidades-maximas">
                      {lote.unidades_maximas ?? "s/d"}
                    </p>
                  </div>
                </div>
                {lote.unidades_maximas && lote.m2_construibles && (
                  <p className="text-[11px] italic text-slate-600 pt-2 mt-2 border-t border-emerald-200">
                    El mínimo de las dos manda al proyectar.
                  </p>
                )}
                {lote.advertencia_sin_unidades && (
                  <p className="text-[11px] font-medium text-amber-900 pt-2 mt-2 border-t border-amber-300"
                     data-testid="advertencia-sin-unidades">
                    ⚠️ IDECOR no informa la cantidad de unidades para este lote (norma condicional). Verificá la ordenanza.
                  </p>
                )}
              </div>
            ) : null}

            <dl className="text-sm space-y-1.5">
              <div className="flex justify-between"><dt className="text-slate-500">FOS<TermHelp text={TERM_GLOSSARY.fos} label="FOS" testid="help-fos" /> · FOT<TermHelp text={TERM_GLOSSARY.fot} label="FOT" testid="help-fot" /></dt><dd>{lote.fos ?? "s/d"} · {lote.fot ?? "s/d"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Altura máx.</dt><dd>{lote.altura_maxima ? `${lote.altura_maxima} m` : (lote.altura_max_txt || "s/d")}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Pisos máx.</dt><dd>{lote.pisos_maximos_num ?? "s/d"} {lote.pisos_maximos_txt && <span className="text-xs text-slate-400">({lote.pisos_maximos_txt})</span>}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Sup. mínima del lote<TermHelp text={TERM_GLOSSARY.sup_min} label="Sup. mínima del lote" testid="help-sup-min" /></dt><dd>{lote.sup_min_m2 ? `${lote.sup_min_m2.toLocaleString("es-AR")} m²` : "s/d"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Frente mínimo<TermHelp text={TERM_GLOSSARY.frente_min} label="Frente mínimo" testid="help-frente-min" /></dt><dd>{lote.frente_min_m ? `${lote.frente_min_m} m` : "s/d"}</dd></div>
              {lote.retiros_txt && (
                <div className="pt-1"><dt className="text-slate-500 text-xs">Retiros<TermHelp text={TERM_GLOSSARY.retiros} label="Retiros" testid="help-retiros" /></dt><dd className="text-xs mt-0.5 whitespace-pre-line">{lote.retiros_txt}</dd></div>
              )}
              {lote.caracter_urbanistico && (
                <div className="pt-1"><dt className="text-slate-500 text-xs">Carácter urbanístico<TermHelp text={TERM_GLOSSARY.caracter_urb} label="Carácter urbanístico" testid="help-caracter-urb" /></dt><dd className="text-xs mt-0.5 whitespace-pre-line">{lote.caracter_urbanistico}</dd></div>
              )}
              {lote.cantidad_unidades_txt && (
                <div className="pt-1"><dt className="text-slate-500 text-xs">Densidad (texto ordenanza)</dt><dd className="text-xs mt-0.5 italic text-slate-600">{lote.cantidad_unidades_txt}</dd></div>
              )}
            </dl>

            {lote.enlace_normativa && (
              <a href={enlaceNormativa(lote.enlace_normativa)} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"
                 className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900"
                 data-testid="btn-pdf-ordenanza">
                <ExternalLink className="w-3 h-3" /> Ver ordenanza oficial (PDF)
              </a>
            )}

            {/* Atribución CC BY-SA 4.0: mostramos que la normativa sale del
                catastro provincial, pero NO exponemos nombres de capas WFS
                (plomería interna). El link a /fuentes cumple la licencia. */}
            {lote.normativa_fuente && (
              <p className="text-[10px] text-slate-400 mt-3">
                Normativa según catastro provincial ·{" "}
                <a href="/fuentes" className="underline hover:text-slate-600">ver fuentes</a>
              </p>
            )}
          </section>

          {/* Consultar en la fuente oficial (Fase 3 · 2026-07-22).
              SOLO LINKS. Nada de fetch al backend de Catastro/Rentas.
              Nada de scraping. Nada de cachear resultado. La consulta la
              hace Agustín a mano pegando el Nº de cuenta que copió arriba.
              Decisión de Gonzalo: es la fuente oficial + sin riesgo legal /
              sin bloqueo / sin Cloudflare. */}
          <section>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600 mb-2">Consultar en fuente oficial</p>
            <div className="space-y-2">
              <a
                href="https://www.catastrocordoba.gob.ar/ca/chequea-los-datos-de-tu-inmueble/"
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                data-testid="btn-consulta-catastro"
                className="w-full border-2 border-slate-700 text-slate-900 hover:bg-slate-900 hover:text-white px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 text-left normal-case font-bold">
                  Catastro <span className="text-slate-500 font-normal">(valuación · datos parcelarios)</span>
                </span>
              </a>
              <a
                href="https://www.rentascordoba.gob.ar/cms/informe-del-inmueble-gc/"
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                data-testid="btn-consulta-rentas"
                className="w-full border-2 border-slate-700 text-slate-900 hover:bg-slate-900 hover:text-white px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
              >
                <FileText className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 text-left normal-case font-bold">
                  Rentas <span className="text-slate-500 font-normal">(informe del inmueble · pagos y deuda)</span>
                </span>
              </a>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Sin costo — links a los sitios oficiales. Pegá el Nº de cuenta ya copiado.
            </p>
          </section>

          <section>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600 mb-2">Verificación visual</p>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2 mb-3">
              ⚠️ BALDÍO = sin mejoras declaradas en el catastro (puede haber construcción sin declarar). Verificá visualmente antes de contactar.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <a href={gmaps} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" data-testid="btn-gmaps" className="border-2 border-slate-700 text-slate-900 hover:bg-slate-900 hover:text-white px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Google Maps
              </a>
              <a href={streetview} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" data-testid="btn-streetview" className="border-2 border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Street View
              </a>
            </div>
          </section>

          {/* Botón "¿Está publicado?" — Sprint UX + Publicado.
              Solo se muestra si hay dirección. El humano decide, el sistema no
              pone badge automático. */}
          <section className="border-t-2 border-slate-300 pt-4">
            {publicadoInfo ? (
              <button
                type="button"
                data-testid="btn-esta-publicado"
                onClick={() => onPublicado && onPublicado(lote)}
                className="w-full border-2 border-slate-900 bg-white hover:bg-slate-900 hover:text-white text-slate-900 px-4 py-3 text-sm font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
              >
                <Globe2 className="w-4 h-4" />
                🔎 ¿Está publicado?
              </button>
            ) : (
              <button
                type="button"
                disabled
                data-testid="btn-esta-publicado-disabled"
                title="Aún se está resolviendo la dirección catastral. Volvé a intentar en unos segundos."
                className="w-full border-2 border-slate-300 bg-slate-50 text-slate-400 px-4 py-3 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 cursor-not-allowed"
              >
                <Globe2 className="w-4 h-4" />
                🔎 ¿Está publicado?
              </button>
            )}
            <p className="text-[11px] text-slate-500 mt-1.5">
              {publicadoInfo
                ? "Busca esta dirección en los 7 portales inmobiliarios."
                : "Disponible cuando se resuelva la dirección del lote."}
            </p>
          </section>

          {lote.es_ruido && (
            <section className="border border-red-500 bg-red-50 p-3">
              <p className="text-xs font-bold text-red-800">Marcado como RUIDO</p>
              <p className="text-xs text-red-700 mt-1">{lote.ruido_motivo}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers de mapa — fitBounds + polígono de zona
// ─────────────────────────────────────────────────────────────
/**
 * FitToBounds — al terminar la búsqueda, vuela a los polígonos de los lotes.
 * Fallback: si no hay lotes, encuadra el polígono de la zona (para que se
 * vea DÓNDE se buscó y que no hay resultados). Nunca queda en la vista
 * provincial.
 *
 * Se dispara UNA vez por `flyKey` (típicamente el zonaId) — no en cada
 * polling de reverse-geocode.
 */
function FitToBounds({ polygons, zonaPolygon, flyKey, onFlown }) {
  const map = useMap();
  useEffect(() => {
    if (!flyKey || flyKey === "__done__") return;
    try {
      if (polygons && polygons.length > 0) {
        const group = L.featureGroup(
          polygons.map((p) => L.polygon(p.positions))
        );
        map.fitBounds(group.getBounds(), { padding: [40, 40], maxZoom: 18 });
      } else if (zonaPolygon) {
        const positions = toLeafletCoords(zonaPolygon);
        if (positions.length) {
          const g = L.featureGroup(positions.map((r) => L.polygon(r)));
          map.fitBounds(g.getBounds(), { padding: [40, 40], maxZoom: 16 });
        }
      }
      onFlown && onFlown(flyKey);
    } catch (e) {
      console.error("FlyToPolygon: fitBounds falló", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyKey]);
  return null;
}

/**
 * RecenterOnZone — al cambiar la zona seleccionada, encuadra su polígono.
 * Portado del patrón de RadarMapa.jsx::RecenterOnZone (INMUEBLES): así los
 * dos motores tienen exactamente el mismo comportamiento de "elegí zona →
 * el mapa vuela sola".
 *
 * Por qué existe además de FitToBounds:
 *  - FitToBounds se dispara con un `flyKey` string y corre 1 vez. Cuando
 *    el user cambia de zona, el effect puede correr con los lotes VIEJOS
 *    todavía en state (loadLotes es async), y vuela al bounds equivocado.
 *  - Este componente reacciona directo al zonaPolygon → siempre correcto,
 *    sin race con la carga de lotes.
 *
 * Coexisten: FitToBounds sigue disparándose al TERMINAR una búsqueda para
 * ajustar a los lotes encontrados. RecenterOnZone se ocupa del cambio de
 * zona en el selector.
 */
function RecenterOnZone({ zona }) {
  const map = useMap();
  const zonaId = zona?.zona_id;
  const zonaPolygon = zona?.poligono;
  useEffect(() => {
    if (!zonaPolygon) return;
    try {
      const positions = toLeafletCoords(zonaPolygon);
      if (!positions.length) return;
      const group = L.featureGroup(positions.map((r) => L.polygon(r)));
      map.fitBounds(group.getBounds(), { padding: [40, 40], maxZoom: 16 });
    } catch (e) {
      // ignore
    }
    // Deps con `zonaPolygon` incluido: si por lo que fuera cambia la
    // geometría (edición de la zona activa), refresca el vuelo.
    // El `key={zonaId}` en el uso (ver JSX) fuerza el remount por cambio
    // de zona → cero closures viejas, cero race con la zona anterior.
    // Bug repro Gonzalo 2026-02-16 "el mapa va una zona atrás" — resuelto.
  }, [zonaId, zonaPolygon, map]);
  return null;
}

// ─────────────────────────────────────────────────────────────
// Página principal
// ─────────────────────────────────────────────────────────────
export default function RadarTierra() {
  const [zonas, setZonas] = useState([]);
  const [zonaId, setZonaId] = useState("");
  const [lotes, setLotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [sinSuperficieEnZona, setSinSuperficieEnZona] = useState(0);
  const [loading, setLoading] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Deep-link: soporta abrir la ficha directo con ?lote=X en la URL.
  // Útil para compartir + para pruebas de honestidad (silencio / loteo).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("lote");
    if (id) setSelectedId(id);
  }, []);

  // Onboarding TOUR: auto-arranque la PRIMERA vez que un usuario entra a
  // Tierra (flag localStorage `landho-tour-visto`). Además escucha el evento
  // `landho:start-tour` disparado desde el sidebar ("Hacé el tour") para
  // relanzarlo a demanda. Robusto: si un elemento no está en el DOM, el paso
  // se saltea (cuenta vacía). Delay para que el layout y el mapa se pinten.
  useEffect(() => {
    let timer;
    if (!hasSeenTour()) {
      timer = setTimeout(() => startLandHoTour(), 800);
    }
    const onStart = () => startLandHoTour();
    window.addEventListener(TOUR_START_EVENT, onStart);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(TOUR_START_EVENT, onStart);
    };
  }, []);
  const [tileMode, setTileMode] = useState(() => localStorage.getItem("radar-tierra-tile") || "sat");
  // filtros dual-range
  const [m2Range, setM2Range] = useState([null, null]);
  const [valM2Range, setValM2Range] = useState([null, null]);
  const [pisosRange, setPisosRange] = useState([null, null]);
  const [unidRange, setUnidRange] = useState([null, null]);
  const [incluirRuido, setIncluirRuido] = useState(false);
  const [soloUrbanizacion, setSoloUrbanizacion] = useState(false);
  // Fase 4a (2026-07-24): filtro "ocultar lotes con protección/histórico/
  // paisajístico/patrimonial" para que Agustín vea solo suelo desarrollable
  // limpio. Client-side (los datos ya vienen; regex barata).
  const [ocultarProteccion, setOcultarProteccion] = useState(false);
  // Fase Rural (2026-07-24): capa nueva de parcelas RURALES desde IDECOR.
  // Live query lazy — solo se llama al prender. Cache backend TTL 5min.
  const [capaRural, setCapaRural] = useState(() => localStorage.getItem("radar-tierra-rural") === "1");
  const [rurales, setRurales] = useState([]);
  const [ruralesLoading, setRuralesLoading] = useState(false);
  const [ruralesStatus, setRuralesStatus] = useState(null); // "ok" | "pendiente"
  const [ruralesLastOkAt, setRuralesLastOkAt] = useState(null);
  const [ruralSel, setRuralSel] = useState(null);
  // Filtro por hectáreas (aparece solo cuando la capa Rural está prendida).
  const [haRange, setHaRange] = useState([null, null]);
  // Fase Hidrografía y Riesgo (2026-02): overlays WMS de IDECOR.
  // 3 toggles independientes, todos apagados por defecto, agrupados en
  // sección colapsable para no cargar el panel visual. Persisten en LS.
  const [wmsCursosAgua, setWmsCursosAgua] = useState(() => localStorage.getItem("radar-tierra-wms-cursos") === "1");
  const [wmsCuencas, setWmsCuencas] = useState(() => localStorage.getItem("radar-tierra-wms-cuencas") === "1");
  const [wmsRiesgoInun, setWmsRiesgoInun] = useState(() => localStorage.getItem("radar-tierra-wms-riesgo") === "1");
  const [hidroPanelOpen, setHidroPanelOpen] = useState(() => localStorage.getItem("radar-tierra-hidro-open") === "1");
  // Fase Publicado (2026-07-24): capa de avisos de TERRENOS EN VENTA de la
  // colección `opportunities` (Modo A del suelo). Lee de nuestra DB, no
  // depende de IDECOR. Estricto: `tipo=terreno` + `operacion=venta`. Naranja
  // #ea580c para no chocar con Rural (verde-oliva) ni catastro (teal).
  // Regla de Honestidad: la capa muestra lo que SÍ está publicado; nunca
  // afirma que un lote sin pin no está en venta.
  const [capaPublicado, setCapaPublicado] = useState(() => localStorage.getItem("radar-tierra-publicado") === "1");
  const [publicados, setPublicados] = useState([]);
  const [publicadosLoading, setPublicadosLoading] = useState(false);
  const [avisoSel, setAvisoSel] = useState(null); // aviso clickeado → ficha lateral
  // Fase Loteos aprobados (2026-07-25): capa violeta con los loteos/
  // fraccionamientos ya aprobados/autorizados por el municipio (IDECOR).
  // Cobertura: solo Córdoba capital (dato municipal). Se lee de la DB
  // (`loteos_aprobados`) — nace derived+stored, cero live query.
  const [capaLoteos, setCapaLoteos] = useState(() => localStorage.getItem("radar-tierra-loteos") === "1");
  const [loteos, setLoteos] = useState([]);
  const [loteosLoading, setLoteosLoading] = useState(false);
  const [loteosStatus, setLoteosStatus] = useState(null);
  const [loteosLastOkAt, setLoteosLastOkAt] = useState(null);
  const [loteoSel, setLoteoSel] = useState(null);
  // Fase Publicado · Captura scoped a TERRENOS (2026-07-24). Reusa el motor
  // de `/opportunities/captura` con los mismos guardarraíles (kill-switch,
  // lock, cancel, audit). Único agregado: `tipos:["terreno"]` en el body
  // para forzar URL scopeada `terrenos-venta-...`. Preview de costo antes
  // de disparar (tope honesto). Polling cada 2s. Refresh de pines al final.
  const [showCostPreview, setShowCostPreview] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureProgress, setCaptureProgress] = useState(null); // {step, message, current, total}
  const [activeCapturaId, setActiveCapturaId] = useState(null);
  const [cancellingCapture, setCancellingCapture] = useState(false);
  // Sprint UX: crear/editar zona + botón Publicado
  const [showZonaDialog, setShowZonaDialog] = useState(false);
  const [editingZona, setEditingZona] = useState(null);
  // Sprint UX: fitBounds — trackea qué zonaId ya voló para no repetir en cada polling
  const [flownFor, setFlownFor] = useState(null);
  // Publicado dialog
  const [publicadoLote, setPublicadoLote] = useState(null);

  useEffect(() => { localStorage.setItem("radar-tierra-tile", tileMode); }, [tileMode]);

  // Limpieza 2026-07-24: la capa "Avisos" (pines de Properati/Zonaprop) se
  // sacó de Tierra. Los 696 avisos siguen vivos en DB y se ven en Inmuebles;
  // Tierra queda solo con SUELO/catastro. Se elimina también la key de
  // localStorage `radar-tierra-avisos` (una-sola-vez, no bloquea si falta).
  useEffect(() => {
    try { localStorage.removeItem("radar-tierra-avisos"); } catch (_) { /* noop */ }
  }, []);

  // Fase Rural — persist toggle + fetch lazy.
  useEffect(() => {
    localStorage.setItem("radar-tierra-rural", capaRural ? "1" : "0");
  }, [capaRural]);

  // Fase Hidrografía y Riesgo — persistencia LS (3 toggles + panel abierto).
  useEffect(() => { localStorage.setItem("radar-tierra-wms-cursos", wmsCursosAgua ? "1" : "0"); }, [wmsCursosAgua]);
  useEffect(() => { localStorage.setItem("radar-tierra-wms-cuencas", wmsCuencas ? "1" : "0"); }, [wmsCuencas]);
  useEffect(() => { localStorage.setItem("radar-tierra-wms-riesgo", wmsRiesgoInun ? "1" : "0"); }, [wmsRiesgoInun]);
  useEffect(() => { localStorage.setItem("radar-tierra-hidro-open", hidroPanelOpen ? "1" : "0"); }, [hidroPanelOpen]);

  useEffect(() => {
    if (!capaRural || !zonaId) { setRurales([]); setRuralesStatus(null); setRuralesLastOkAt(null); return; }
    let cancelled = false;
    setRuralesLoading(true);
    api.get(`/lotes/rurales`, { params: { zona_id: zonaId } })
      .then(r => {
        if (cancelled) return;
        setRurales(r.data.items || []);
        setRuralesStatus(r.data.status || null);
        setRuralesLastOkAt(r.data.last_ok_at || null);
      })
      .catch(err => {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || "No pude cargar capa Rural");
        setRurales([]);
        setRuralesStatus(null);
        setRuralesLastOkAt(null);
      })
      .finally(() => { if (!cancelled) setRuralesLoading(false); });
    return () => { cancelled = true; };
  }, [capaRural, zonaId]);

  // Fase Publicado — persist + fetch lazy (solo terrenos en venta).
  useEffect(() => {
    localStorage.setItem("radar-tierra-publicado", capaPublicado ? "1" : "0");
  }, [capaPublicado]);

  useEffect(() => {
    if (!capaPublicado || !zonaId) { setPublicados([]); setAvisoSel(null); return; }
    let cancelled = false;
    setPublicadosLoading(true);
    api.get(`/opportunities/map`, {
      params: { zona_id: zonaId, tipos: "terreno", operacion: "venta", limit: 2000 },
    })
      .then(r => {
        if (cancelled) return;
        setPublicados(r.data.items || []);
      })
      .catch(err => {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || "No pude cargar capa Publicado");
        setPublicados([]);
      })
      .finally(() => { if (!cancelled) setPublicadosLoading(false); });
    return () => { cancelled = true; };
  }, [capaPublicado, zonaId]);

  // Fase Loteos aprobados — persist + fetch lazy (lee de DB, no IDECOR).
  useEffect(() => { localStorage.setItem("radar-tierra-loteos", capaLoteos ? "1" : "0"); }, [capaLoteos]);

  useEffect(() => {
    if (!capaLoteos || !zonaId) { setLoteos([]); setLoteoSel(null); setLoteosStatus(null); setLoteosLastOkAt(null); return; }
    let cancelled = false;
    setLoteosLoading(true);
    api.get(`/lotes/loteos`, { params: { zona_id: zonaId } })
      .then(r => {
        if (cancelled) return;
        setLoteos(r.data.items || []);
        setLoteosStatus(r.data.status || null);
        setLoteosLastOkAt(r.data.last_ok_at || null);
      })
      .catch(err => {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || "No pude cargar capa Loteos");
        setLoteos([]);
        setLoteosStatus(null);
        setLoteosLastOkAt(null);
      })
      .finally(() => { if (!cancelled) setLoteosLoading(false); });
    return () => { cancelled = true; };
  }, [capaLoteos, zonaId]);

  useEffect(() => {
    api.get("/zonas").then((r) => setZonas(r.data?.items || r.data || []))
      .catch(() => setZonas([]));
  }, []);

  const loadLotes = async () => {
    if (!zonaId) { setLotes([]); return; }
    setLoading(true);
    try {
      const params = { zona_id: zonaId, incluir_ruido: incluirRuido, solo_urbanizacion: soloUrbanizacion, limit: 5000 };
      if (m2Range[0] != null) params.m2_min = m2Range[0];
      if (m2Range[1] != null) params.m2_max = m2Range[1];
      if (valM2Range[0] != null) params.val_m2_min = valM2Range[0];
      if (valM2Range[1] != null) params.val_m2_max = valM2Range[1];
      if (pisosRange[0] != null) params.pisos_min = pisosRange[0];
      if (pisosRange[1] != null) params.pisos_max = pisosRange[1];
      if (unidRange[0] != null) params.unidades_min = unidRange[0];
      if (unidRange[1] != null) params.unidades_max = unidRange[1];
      const r = await api.get("/lotes", { params });
      setLotes(r.data.items || []);
      setTotal(r.data.total || 0);
      setSinSuperficieEnZona(r.data.sin_superficie_en_zona || 0);
    } catch (e) {
      toast.error("No pudimos cargar los lotes");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadLotes(); /* eslint-disable-next-line */ }, [zonaId, incluirRuido, soloUrbanizacion, JSON.stringify(m2Range), JSON.stringify(valM2Range), JSON.stringify(pisosRange), JSON.stringify(unidRange)]);

  // Polling: mientras haya lotes sin `direccion`, refrescamos cada 5s para
  // recibir los updates del reverse-geocoder (background task). Se detiene
  // cuando todos tienen dirección o pasa el timeout (5 min).
  useEffect(() => {
    if (!lotes.length) return;
    const pendientes = lotes.some(l => l.direccion == null);
    if (!pendientes) return;
    const start = Date.now();
    const TIMEOUT_MS = 5 * 60 * 1000;
    const t = setInterval(() => {
      if (Date.now() - start > TIMEOUT_MS) { clearInterval(t); return; }
      loadLotes();
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [lotes.length, JSON.stringify(lotes.map(l => `${l.lote_id}:${l.direccion == null ? 0 : 1}`))]);

  // Derivamos `selected` del array `lotes` para que el polling refresque
  // automáticamente los datos de la ficha (dirección/barrio) cuando llegan.
  // Si el lote NO está en `lotes` (deep-link ?lote=X sin cargar la zona),
  // hacemos fetch directo del doc.
  const [selectedDirect, setSelectedDirect] = useState(null);
  useEffect(() => {
    if (!selectedId) { setSelectedDirect(null); return; }
    const inList = lotes.find(l => l.lote_id === selectedId);
    if (inList) { setSelectedDirect(null); return; }
    api.get(`/lotes/${selectedId}`).then(r => setSelectedDirect(r.data)).catch((e) => { console.error(`GET /lotes/${selectedId} falló`, e); setSelectedDirect(null); });
  }, [selectedId, lotes]);

  const selected = useMemo(
    () => (selectedId
      ? (lotes.find(l => l.lote_id === selectedId) || selectedDirect || null)
      : null),
    [selectedId, lotes, selectedDirect]
  );

  const buscarEnZona = async () => {
    if (!zonaId) { toast.error("Elegí una zona primero."); return; }
    setBuscando(true);
    try {
      const r = await api.post("/lotes/buscar", { zona_id: zonaId });
      const d = r.data;
      // Toast honesto: solo N lotes en la zona. `filtrados_fuera_zona` va a
      // /radar/actividad como diagnóstico interno (decisión Gonzalo).
      toast.success(
        `${d.guardados} lote${d.guardados === 1 ? "" : "s"} en la zona · sin costo (IDECOR)`,
        { duration: 5000 }
      );
      await loadLotes();
      // Dispara el fitBounds — voló al conjunto que acabamos de guardar.
      setFlownFor(`buscar_${zonaId}_${Date.now()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al buscar en IDECOR");
    } finally { setBuscando(false); }
  };

  // Zona activa (para dibujar el polígono en el mapa + prefill del editor)
  const zonaActiva = useMemo(
    () => zonas.find(z => z.zona_id === zonaId) || null,
    [zonas, zonaId]
  );
  const zonaPositions = useMemo(
    () => (zonaActiva?.poligono ? toLeafletCoords(zonaActiva.poligono) : []),
    [zonaActiva]
  );

  // Al cambiar la zona seleccionada, el `RecenterOnZone` (useMap + useEffect
  // en `zonaActiva.poligono`) ya vuela el mapa a la zona nueva de forma
  // sincrónica. NO seteamos `flyKey` acá porque disparaba también `FitToBounds`
  // que corría con `polygons` VIEJOS (loadLotes async) y pisaba el vuelo a
  // la zona anterior — bug repro Gonzalo 2026-02-16: "el mapa va una zona
  // atrás". `FitToBounds` queda reservado para el fin de una BÚSQUEDA de
  // lotes (que sí necesita fit a los lotes encontrados).

  // Callback cuando ZonaDialog guarda: seleccionar la nueva zona y disparar búsqueda
  const handleZonaSaved = async (zonaSaved) => {
    // Refrescar lista
    const r = await api.get("/zonas");
    setZonas(r.data?.items || r.data || []);
    setZonaId(zonaSaved.zona_id);
    setEditingZona(null);
    // Auto-búsqueda tras crear (solo si es nueva; edición no dispara auto)
    if (!editingZona) {
      setBuscando(true);
      try {
        await api.post("/lotes/buscar", { zona_id: zonaSaved.zona_id });
      } catch { /* toast lo maneja loadLotes */ }
      finally { setBuscando(false); }
    }
    await loadLotes();
    setFlownFor(`saved_${zonaSaved.zona_id}_${Date.now()}`);
  };
  handleZonaSaved._preview = () => {};  // no-op — Tierra no muestra preview live
  handleZonaSaved._pin = () => {};

  // Marca cada polígono con `protegido` (bandera derivada) para que
  // el render sepa dibujarlo distinto sin recalcular el regex por frame.
  const polygons = useMemo(() =>
    lotes.filter(l => l.geometry && l.geometry.coordinates)
      .map(l => ({
        lote: l,
        positions: toLeafletCoords(l.geometry),
        protegido: tieneProteccionEspecial(l),
      })),
  [lotes]);

  // Filtro cliente: ocultar lotes con protección cuando el usuario lo pide.
  // Aplica después del `polygons` para no perder la clasificación
  // (el conteo total sigue viendo el universo real).
  const polygonsVisibles = useMemo(
    () => ocultarProteccion ? polygons.filter(p => !p.protegido) : polygons,
    [polygons, ocultarProteccion],
  );
  const protegidosCount = useMemo(
    () => polygons.filter(p => p.protegido).length,
    [polygons],
  );

  // Rural: filtro client-side por ha (default null=todos).
  const ruralesFiltrados = useMemo(() => {
    const [minHa, maxHa] = haRange;
    if (minHa == null && maxHa == null) return rurales;
    return rurales.filter(r => {
      const ha = r.superficie_ha;
      if (ha == null) return false;
      if (minHa != null && ha < minHa) return false;
      if (maxHa != null && ha > maxHa) return false;
      return true;
    });
  }, [rurales, haRange]);

  // Loteos: breakdown por origen (aprobados vs autorizados) — se cuenta en
  // el front desde los items (el endpoint ya devuelve source_layer).
  // Formato: "3 aprobados · 1 autorizado" (singular/plural respetado).
  const loteosBreakdown = useMemo(() => {
    if (!loteos.length) return null;
    let apr = 0, aut = 0;
    for (const lo of loteos) {
      if (lo.source_layer === "loteos_muni_aprobados") apr++;
      else if (lo.source_layer === "loteos_muni_autorizados") aut++;
    }
    if (apr === 0 && aut === 0) return null;
    const parts = [];
    if (apr > 0) parts.push(`${apr} aprobado${apr === 1 ? "" : "s"}`);
    if (aut > 0) parts.push(`${aut} autorizado${aut === 1 ? "" : "s"}`);
    return parts.join(" · ");
  }, [loteos]);

  const center = polygons.length > 0
    ? (() => {
        const c = polygons[0].lote.centroide?.coordinates;
        return c ? [c[1], c[0]] : CBA;
      })()
    : CBA;

  // Preview honesto — mismo tope que RadarMapa (Properati $0.0015/item ×
  // 100 = US$0.15). Zonaprop off desde 15/07 por data basura, no lo
  // incluimos. Es tope, no promesa: el usuario puede cancelar durante la
  // corrida y solo paga los ítems ya devueltos.
  const capturaCostPreview = { runs: [{ name: "Properati", maxItems: 100, perItem: 0.0015 }], total: 0.15 };

  const capturarTerrenos = async () => {
    if (!zonaId || capturing) return;
    const zonaNombre = zonas.find((z) => z.zona_id === zonaId)?.nombre || zonaId;
    setCapturing(true);
    setCaptureProgress({ step: "queued", message: "Encolando búsqueda…", current: 0, total: 0 });
    const started = Date.now();
    try {
      // Dispara captura scopeada a TERRENOS. Backend override en memoria:
      // zona.default_tipos = ["terreno"] → URL "terrenos-venta-...".
      const res = await api.post("/opportunities/captura", {
        zona_id: zonaId,
        tipos: ["terreno"],
      });
      const capturaId = res.data?.captura_id;
      if (!capturaId) throw new Error("El servidor no devolvió captura_id");
      setActiveCapturaId(capturaId);

      // Polling cada 2s. Timeout de seguridad: 10 min.
      const POLL_MS = 2000;
      const MAX_MS = 10 * 60 * 1000;
      let last = null;
      while (Date.now() - started < MAX_MS) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        try {
          const st = await api.get(`/opportunities/captura/${capturaId}`, { timeout: 10000 });
          last = st.data;
          setCaptureProgress(last.progreso || null);
          if (last.status === "completada") break;
        } catch (pollErr) {
          const isNetTimeout = pollErr?.code === "ECONNABORTED" || pollErr?.code === "ERR_NETWORK";
          if (!isNetTimeout) throw pollErr;
        }
        if (last?.status === "cancelada") {
          const cost = last.resultado?.costo_total_usd ?? 0;
          throw new Error(`Búsqueda cancelada · costo hasta abort: $${Number(cost).toFixed(4)}`);
        }
        if (last?.status === "error") {
          throw new Error(last.error || "La búsqueda falló");
        }
      }
      if (!last || last.status === "en_curso") {
        throw new Error("La búsqueda tardó más de lo esperado. Volvé a intentar en unos minutos.");
      }
      const s = last.resultado || {};
      const nuevos = s.persistidos_nuevos ?? 0;
      // Regla de Honestidad: si el portal no cubre la zona → mensaje honesto,
      // NUNCA "no hay terrenos en venta".
      if (nuevos === 0) {
        toast.info(`«${zonaNombre}»: el portal no tiene terrenos publicados en esta zona.`);
      } else {
        toast.success(`Listo: ${nuevos} terreno${nuevos === 1 ? "" : "s"} publicado${nuevos === 1 ? "" : "s"} traído${nuevos === 1 ? "" : "s"}.`);
      }
      // Refresh de la capa Publicado (mismos filtros).
      try {
        const r = await api.get(`/opportunities/map`, {
          params: { zona_id: zonaId, tipos: "terreno", operacion: "venta", limit: 2000 },
        });
        setPublicados(r.data.items || []);
      } catch (refreshErr) { console.error("refetch publicados post-captura falló", refreshErr); /* el toast del catch principal ya cubre el error visible al user */ }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 423) {
        toast.error("Apify está apagado. Solo un admin puede habilitarlo en /radar/ajustes.");
      } else if (e?.response?.status === 409) {
        const msg = (typeof d === "object" && d?.message) ? d.message : "Ya hay una captura corriendo sobre esta zona.";
        toast.error(msg);
      } else {
        const msg = typeof d === "string" ? d : d?.message || d?.detail || e?.message || "No pudimos completar la búsqueda.";
        toast.error(msg);
      }
    } finally {
      setCapturing(false);
      setCaptureProgress(null);
      setActiveCapturaId(null);
      setCancellingCapture(false);
    }
  };

  const cancelCapture = async () => {
    if (!activeCapturaId || cancellingCapture) return;
    if (!window.confirm("¿Cancelar la búsqueda? Se aborta el run de Apify (se deja de cobrar) y se registra el costo hasta el abort.")) return;
    setCancellingCapture(true);
    try {
      const res = await api.post(`/opportunities/captura/${activeCapturaId}/cancel`);
      const cost = res.data?.costo_hasta_abort_usd ?? 0;
      toast.success(`Búsqueda cancelada · costo $${Number(cost).toFixed(4)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "No pude cancelar");
      setCancellingCapture(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="radar-tierra-page">
      <header className="border-b border-slate-300 bg-white px-6 py-5">
        <div className="flex items-center gap-3">
          <Trees className="w-6 h-6 text-emerald-700" />
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Tierra</h1>
            <p className="text-sm text-slate-600">Datos oficiales del catastro de la Provincia de Córdoba. Sin costo.</p>
          </div>
        </div>
      </header>

      <div className="flex" style={{ height: "calc(100vh - 96px)" }}>
        {/* Panel filtros */}
        <aside className="w-80 border-r border-slate-300 bg-white p-5 overflow-y-auto flex-shrink-0" data-testid="lote-filters-panel">
          <div className="mb-5" data-tour="zona-controls">
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1.5 block">Zona</label>
            <select
              data-testid="lote-select-zona"
              value={zonaId}
              onChange={(e) => setZonaId(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">— elegir zona —</option>
              {zonas.map(z => <option key={z.zona_id} value={z.zona_id}>{z.nombre}</option>)}
            </select>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                data-testid="btn-nueva-zona"
                onClick={() => { setEditingZona(null); setShowZonaDialog(true); }}
                className="flex-1 border border-slate-300 hover:border-emerald-700 hover:text-emerald-700 px-2 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Nueva zona
              </button>
              <button
                type="button"
                data-testid="btn-editar-zona"
                onClick={() => { setEditingZona(zonaActiva); setShowZonaDialog(true); }}
                disabled={!zonaActiva}
                className="flex-1 border border-slate-300 hover:border-emerald-700 hover:text-emerald-700 px-2 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Pencil className="w-3.5 h-3.5" /> Editar
              </button>
              <button
                type="button"
                data-testid="btn-borrar-zona"
                onClick={async () => {
                  if (!zonaActiva) return;
                  if (!window.confirm(`Borrar la zona "${zonaActiva.nombre}"? Los avisos y lotes asociados NO se borran.`)) return;
                  try {
                    await api.delete(`/zonas/${zonaActiva.zona_id}`);
                    toast.success(`Zona "${zonaActiva.nombre}" borrada`);
                    setZonaId("");
                    setZonas(zs => zs.filter(z => z.zona_id !== zonaActiva.zona_id));
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "No se pudo borrar");
                  }
                }}
                disabled={!zonaActiva}
                className="border border-slate-300 hover:border-red-700 hover:text-red-700 px-2 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={zonaActiva ? `Borrar "${zonaActiva.nombre}"` : "Elegí una zona"}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <Button
            data-testid="btn-buscar-idecor"
            onClick={buscarEnZona}
            disabled={!zonaId || buscando}
            className="w-full rounded-none bg-emerald-700 hover:bg-emerald-800 text-white h-11 mb-1"
          >
            {buscando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Buscar lotes en esta zona
          </Button>
          <p className="text-[11px] text-emerald-800 mb-5 border-l-2 border-emerald-600 pl-2">
            <strong>Sin costo</strong> — datos catastrales oficiales de la Provincia.
          </p>

          <div data-tour="filtros">
          <RangeField label="Superficie (m²)" value={m2Range} onChange={setM2Range} testidPrefix="filter-m2" />
          {/* Filtro por hectáreas — solo visible cuando la capa Rural está
              activa. Agustín piensa los campos en ha, no en m². Filter
              client-side sobre `rurales`. */}
          {capaRural && (
            <RangeField
              label="Superficie rural (ha)"
              value={haRange}
              onChange={setHaRange}
              testidPrefix="filter-ha"
            />
          )}
          <RangeField label="Valor por m² (ARS)" value={valM2Range} onChange={setValM2Range} testidPrefix="filter-valm2" />
          <RangeField label="Pisos permitidos" value={pisosRange} onChange={setPisosRange} testidPrefix="filter-pisos" />
          <RangeField label="Unidades máximas" value={unidRange} onChange={setUnidRange} testidPrefix="filter-unid" />

          <div className="border-t border-slate-200 pt-4 mt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="chk-solo-urb">
              <input type="checkbox" checked={soloUrbanizacion} onChange={(e) => setSoloUrbanizacion(e.target.checked)} className="w-4 h-4 accent-emerald-700" />
              <Sparkles className="w-4 h-4 text-emerald-700" />
              <span className="font-medium">Solo zonas en urbanización ⭐</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="chk-incluir-ruido">
              <input type="checkbox" checked={incluirRuido} onChange={(e) => setIncluirRuido(e.target.checked)} className="w-4 h-4 accent-slate-700" />
              <span>Mostrar ruido (parques, calles, etc.)</span>
            </label>
            {/* Filtro protección — Fase 4a (2026-07-24). Copy honesto:
                oculta los lotes que muestran la alerta patrimonial/paisajística
                en la ficha. Combina con m² y demás filtros. Default apagado. */}
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="chk-ocultar-proteccion">
              <input type="checkbox" checked={ocultarProteccion} onChange={(e) => setOcultarProteccion(e.target.checked)} className="w-4 h-4 accent-red-600" />
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span>Ocultar lotes con protección</span>
            </label>
          </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200 text-sm">
            <p className="text-slate-600" data-testid="lote-total-count">
              {loading ? <span className="italic">Cargando…</span> : (
                <>
                  <strong className="text-emerald-800 text-lg">{total}</strong> lote{total !== 1 && "s"} en la zona
                  {lotes.length < total && (
                    <span className="text-slate-500"> — mostrando {lotes.length}</span>
                  )}
                  {ocultarProteccion && protegidosCount > 0 && (
                    <span className="text-slate-500"> — {protegidosCount} con protección oculto{protegidosCount !== 1 && "s"}</span>
                  )}
                </>
              )}
            </p>
            {/* Cartel honesto: si el server nos cortó por limit, avisamos
                en pantalla. Nunca ocultar en silencio (regla Gonzalo). */}
            {!loading && lotes.length < total && (
              <div className="mt-2 text-xs bg-amber-50 border-l-2 border-amber-500 p-2" data-testid="lote-limit-warn">
                <p className="font-bold text-amber-900">Mapa acotado por performance</p>
                <p className="text-amber-800 mt-0.5">
                  Se dibujan los {lotes.length.toLocaleString("es-AR")} más grandes de {total.toLocaleString("es-AR")}.
                  Aplicá filtros (m², valuación) para ver el resto.
                </p>
              </div>
            )}
            {/* Cartel honesto Fase 2: si el filtro por m² está activo y
                hay lotes en la zona SIN superficie informada, avisamos que
                esos se excluyen (Mongo $gte/$lte descarta null). No los
                ocultamos en silencio. */}
            {!loading && (m2Range[0] != null || m2Range[1] != null) && sinSuperficieEnZona > 0 && (
              <div className="mt-2 text-xs bg-slate-100 border-l-2 border-slate-400 p-2" data-testid="lote-sin-sup-warn">
                <p className="text-slate-700">
                  <strong>{sinSuperficieEnZona}</strong> lote{sinSuperficieEnZona !== 1 && "s"} sin superficie informada en esta zona — no aparece{sinSuperficieEnZona === 1 ? "" : "n"} al filtrar por m².
                </p>
              </div>
            )}
          </div>
        </aside>

        {/* Mapa */}
        <div className="flex-1 relative">
          {/* Fix bug 2026-07-24 (superposición ficha ↔ controles): mientras
              hay una ficha lateral abierta (lote / aviso / rural), OCULTAMOS
              el panel de controles de capas. Motivo: los controles están
              `absolute top-3 right-3` y las fichas laterales cubren el
              borde derecho — con z-[400] los controles sobreescriben las
              fichas z-30 (aviso/rural). En lugar de subirle z-index a las
              fichas y que los controles queden atrás (visualmente igual de
              feos por medio-cubiertos), preferimos la opción "modo lectura":
              controles ocultos → ficha limpia. Al cerrar la ficha vuelven a
              su lugar completos, sin tocar sus posiciones ni tamaños. */}
          {!(selected || avisoSel || ruralSel || loteoSel) && (
          <div className="absolute top-3 right-3 z-[400] flex flex-col gap-2 items-end" data-testid="tierra-controles-capas">
            {/* Toggle tile satélite/calles */}
            <div className="flex bg-white border border-slate-300 shadow">
              <button onClick={() => setTileMode("sat")} data-testid="tile-sat"
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${tileMode === "sat" ? "bg-emerald-700 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                Satélite
              </button>
              <button onClick={() => setTileMode("streets")} data-testid="tile-streets"
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${tileMode === "streets" ? "bg-emerald-700 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                Calles
              </button>
            </div>

            {/* Toggle capa Rural (2026-07-24) — parcelas de campo IDECOR.
                Lazy load: no llama al endpoint hasta que se prende. */}
            <div className="bg-white border border-slate-300 shadow">
              <button
                onClick={() => setCapaRural(v => !v)}
                data-testid="toggle-capa-rural"
                className={`w-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${capaRural ? "bg-lime-700 text-white" : "text-slate-700 hover:bg-slate-100"}`}
              >
                <span className={`w-2 h-2 rounded-full ${capaRural ? "bg-white" : "bg-lime-600"}`}></span>
                Rural
                {capaRural && (
                  ruralesLoading
                    ? <Loader2 className="w-3 h-3 animate-spin ml-auto" />
                    : <span className="ml-auto font-mono">({ruralesFiltrados.length})</span>
                )}
              </button>
              {capaRural && rurales.length === 0 && !ruralesLoading && (
                ruralesStatus === "pendiente" ? (
                  <p className="text-[10px] text-amber-800 bg-amber-50 border-t border-slate-200 px-2 py-1.5 italic" data-testid="rural-status-pendiente">
                    Estamos preparando los datos rurales de esta zona — van a estar disponibles apenas IDECOR responda. Reintentá en un momento.
                  </p>
                ) : (
                  // Honestidad 2026-07-26: distinguimos "pendiente" (aún no
                  // sabemos → invita a reintentar) de "ok + total=0" (dato
                  // FINAL: se derivó bien y esta zona no tiene rural, típico
                  // de barrios urbanos). El mensaje "No hay…" cierra sin
                  // prometer nada que no vaya a llegar.
                  <p className="text-[10px] text-slate-500 border-t border-slate-200 px-2 py-1.5 italic" data-testid="rural-empty-final">
                    No hay parcelas rurales en esta zona.
                  </p>
                )
              )}
              {/* Frescura del dato — solo si tenemos last_ok_at real.
                  Regla de honestidad: si status="pendiente" NO mostramos
                  fecha; si el dato es viejo, la antigüedad se muestra tal
                  cual ("hace 3 días") — no se maquilla. */}
              {capaRural && !ruralesLoading && ruralesLastOkAt && (
                <p className="text-[9px] text-slate-500 border-t border-slate-200 px-2 py-1 leading-tight" data-testid="rural-frescura">
                  Datos actualizados {fmtFrescura(ruralesLastOkAt)}.
                </p>
              )}
            </div>

            {/* Toggle capa Publicado (2026-07-24) — terrenos EN VENTA de la
                colección `opportunities` (lo que Sí está publicado en portales).
                Estricto: tipo=terreno + operacion=venta. Lee de nuestra DB.
                Naranja para diferenciar de Rural (verde-oliva) y catastro
                (teal). Regla de Honestidad: nunca afirma que un lote sin pin
                no está en venta — solo mostramos lo que SÍ existe. */}
            <div className="bg-white border border-slate-300 shadow w-[220px]">
              <button
                onClick={() => setCapaPublicado(v => !v)}
                data-testid="toggle-capa-publicado"
                className={`w-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${capaPublicado ? "bg-orange-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}
              >
                <span className={`w-2 h-2 rounded-full ${capaPublicado ? "bg-white" : "bg-orange-600"}`}></span>
                Publicado
                {capaPublicado && (
                  publicadosLoading
                    ? <Loader2 className="w-3 h-3 animate-spin ml-auto" />
                    : <span className="ml-auto font-mono" data-testid="publicado-count">({publicados.length})</span>
                )}
              </button>
              {capaPublicado && !publicadosLoading && publicados.length === 0 && zonaId && (
                <p className="text-[10px] text-slate-600 border-t border-slate-200 px-2 py-1.5 italic" data-testid="publicado-empty">
                  Sin terrenos publicados en esta zona — capturá para traerlos.
                </p>
              )}
              {/* Botón "Traer terrenos publicados de esta zona" — reusa el
                  motor de captura (kill-switch + lock + preview + cancel +
                  audit) con override `tipos:["terreno"]` para scopear la
                  URL a `terrenos-venta-…`. Aparece cuando la capa está
                  prendida y hay zona elegida. Ver logic arriba. */}
              {capaPublicado && zonaId && !capturing && (
                <div className="border-t border-slate-200 p-2">
                  <button
                    type="button"
                    onClick={() => setShowCostPreview(true)}
                    data-testid="btn-capturar-terrenos"
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white text-[11px] font-bold uppercase tracking-wider px-2 py-2 flex items-center justify-center gap-1.5"
                    title="Consulta Properati/Zonaprop y trae los terrenos publicados"
                  >
                    <Search className="w-3.5 h-3.5" />
                    Traer terrenos publicados
                  </button>
                  <p className="text-[9px] text-slate-500 mt-1 italic leading-tight">
                    Gasta ~US$ 0.15 (tope). Cancelable durante la corrida.
                  </p>
                </div>
              )}
              {capaPublicado && capturing && (
                <div className="border-t border-slate-200 p-2" data-testid="capture-progress">
                  <div className="flex items-center gap-2 text-[11px] text-slate-700 mb-1">
                    <Loader2 className="w-3 h-3 animate-spin text-orange-600" />
                    <span className="font-bold uppercase tracking-wider">Capturando…</span>
                  </div>
                  {captureProgress?.message && (
                    <p className="text-[10px] text-slate-600 leading-tight" data-testid="capture-progress-msg">
                      {captureProgress.message}
                      {captureProgress.total > 0 && (
                        <span className="ml-1 font-mono">({captureProgress.current}/{captureProgress.total})</span>
                      )}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={cancelCapture}
                    disabled={cancellingCapture || !activeCapturaId}
                    data-testid="btn-cancelar-captura"
                    className="mt-2 w-full border border-red-700 text-red-700 hover:bg-red-700 hover:text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cancellingCapture ? "Cancelando…" : "Cancelar búsqueda"}
                  </button>
                </div>
              )}
              {capaPublicado && (
                <p className="text-[9px] text-slate-500 border-t border-slate-200 px-2 py-1 leading-tight">
                  Terrenos en venta (Properati/Zonaprop). La ausencia de pin no significa que un lote no esté a la venta.
                </p>
              )}
            </div>

            {/* Toggle capa Loteos aprobados (2026-07-25) — polígonos violetas
                de fraccionamientos aprobados/autorizados por el municipio
                (IDECOR). Cobertura: solo Córdoba capital (dato municipal).
                Lee de nuestra DB (`loteos_aprobados`), nunca live. */}
            <div className="bg-white border border-slate-300 shadow w-[220px]">
              <button
                onClick={() => setCapaLoteos(v => !v)}
                data-testid="toggle-capa-loteos"
                className={`w-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${capaLoteos ? "bg-purple-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}
              >
                <span className={`w-2 h-2 rounded-full ${capaLoteos ? "bg-white" : "bg-purple-600"}`}></span>
                Loteos aprobados
                {capaLoteos && (
                  loteosLoading
                    ? <Loader2 className="w-3 h-3 animate-spin ml-auto" />
                    : <span className="ml-auto font-mono" data-testid="loteos-count">({loteos.length})</span>
                )}
              </button>
              {/* Breakdown por origen (2026-07-25): solo aparece si al
                  menos un origen tiene cuenta. Le da al usuario el pulso
                  del desarrollo (aprobados vs autorizados) sin abrir ficha. */}
              {capaLoteos && !loteosLoading && loteosBreakdown && (
                <p className="text-[10px] text-slate-600 border-t border-slate-200 px-2 py-1 font-mono" data-testid="loteos-breakdown">
                  {loteosBreakdown}
                </p>
              )}
              {capaLoteos && !loteosLoading && loteos.length === 0 && zonaId && (
                loteosStatus === "pendiente" ? (
                  <p className="text-[10px] text-amber-800 bg-amber-50 border-t border-slate-200 px-2 py-1.5 italic" data-testid="loteos-status-pendiente">
                    Estamos preparando los datos de loteos — disponibles apenas IDECOR responda.
                  </p>
                ) : (
                  // Honestidad 2026-07-26: idem rural. `status="ok"` + 0
                  // items = dato final ("se derivó y no hay"), no una
                  // invitación a esperar (típico fuera de Córdoba capital).
                  <p className="text-[10px] text-slate-600 border-t border-slate-200 px-2 py-1.5 italic" data-testid="loteos-empty-final">
                    No hay loteos aprobados en esta zona.
                  </p>
                )
              )}
              {capaLoteos && (
                <p className="text-[9px] text-slate-500 border-t border-slate-200 px-2 py-1 leading-tight">
                  Cobertura solo Córdoba capital (dato municipal IDECOR).
                </p>
              )}
              {/* Frescura loteos — misma regla honestidad que rural. */}
              {capaLoteos && !loteosLoading && loteosLastOkAt && (
                <p className="text-[9px] text-slate-500 border-t border-slate-200 px-2 py-1 leading-tight" data-testid="loteos-frescura">
                  Datos actualizados {fmtFrescura(loteosLastOkAt)}.
                </p>
              )}
            </div>

            {/* Sección Hidrografía y Riesgo (2026-02) — overlays WMS IDECOR.
                Colapsable (default cerrado) para no cargar el panel visual.
                3 toggles independientes; los datos vienen del GeoServer
                oficial de IDECOR (workspace `idecor`). Cobertura de riesgo
                de inundación es MUNICIPAL PARCIAL (Unquillo + Carlos Paz);
                el warning va en la leyenda cuando la capa está prendida. */}
            <div className="bg-white border border-slate-300 shadow w-[220px]" data-testid="hidro-panel">
              <button
                onClick={() => setHidroPanelOpen(v => !v)}
                data-testid="hidro-panel-toggle"
                className="w-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 text-slate-700 hover:bg-slate-100 border-b border-slate-200"
              >
                <Waves className="w-3.5 h-3.5 text-sky-700" />
                Hidrografía y Riesgo
                {(wmsCursosAgua || wmsCuencas || wmsRiesgoInun) && (
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-600" title="Capas activas" />
                )}
                {hidroPanelOpen
                  ? <ChevronDown className="w-3.5 h-3.5 ml-auto" />
                  : <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
              </button>
              {hidroPanelOpen && (
                <div className="px-2 py-2 space-y-1">
                  <label className="flex items-center gap-2 px-1 py-1 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="wms-toggle-cursos-agua">
                    <input
                      type="checkbox"
                      checked={wmsCursosAgua}
                      onChange={(e) => setWmsCursosAgua(e.target.checked)}
                      className="accent-sky-700"
                    />
                    <span className="w-3 h-0.5 bg-sky-600 rounded-full inline-block" aria-hidden />
                    <span>Cursos de agua</span>
                  </label>
                  <label className="flex items-center gap-2 px-1 py-1 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="wms-toggle-cuencas">
                    <input
                      type="checkbox"
                      checked={wmsCuencas}
                      onChange={(e) => setWmsCuencas(e.target.checked)}
                      className="accent-blue-700"
                    />
                    <span className="w-3 h-3 border border-blue-700 bg-blue-200/60 inline-block" aria-hidden />
                    <span>Cuencas hidrográficas</span>
                  </label>
                  <label className="flex items-center gap-2 px-1 py-1 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="wms-toggle-riesgo-inundacion">
                    <input
                      type="checkbox"
                      checked={wmsRiesgoInun}
                      onChange={(e) => setWmsRiesgoInun(e.target.checked)}
                      className="accent-red-700"
                    />
                    <span className="w-3 h-3 border border-red-700 bg-red-300/60 inline-block" aria-hidden />
                    <span>Riesgo de inundación</span>
                  </label>
                  {wmsRiesgoInun && (
                    <div className="mt-1 border-t border-slate-200 pt-2 px-1" data-testid="wms-riesgo-legend">
                      <div className="flex items-start gap-1.5 text-[10px] text-slate-600 leading-tight">
                        <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0 mt-0.5" />
                        <p>
                          Cobertura municipal <strong>parcial</strong> (Unquillo + Villa Carlos Paz). La ausencia de color <strong>no implica</strong> ausencia de riesgo — consultar defensa civil local.
                        </p>
                      </div>
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 italic pt-1 border-t border-slate-100 px-1">
                    Fuente: IDECOR (mapascordoba.gob.ar).
                  </p>
                </div>
              )}
            </div>

            {/* Panel de capas de suelo — hoy solo "catastro" (implícito, siempre
                on). Preparado para sumar capas de IDECOR: rural, riesgo hídrico,
                loteos. Fase limpieza avisos 2026-07-24 — quitados los pines y
                el toggle "Avisos" para que este panel sea sólo de SUELO.
                Los 696 avisos siguen vivos en DB y en Inmuebles. */}

            {/* Leyenda protección/condición especial — visible cuando hay
                lotes protegidos en la zona (o cuando el filtro los ocultó).
                Fase 4a 2026-07-24. Copy honesto: condición A VERIFICAR. */}
            {protegidosCount > 0 && (
              <div className="bg-white border border-slate-300 shadow px-3 py-2 max-w-[220px]" data-testid="legend-proteccion">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-red-700">
                  <svg width="24" height="10" className="shrink-0" aria-hidden>
                    <line x1="0" y1="5" x2="24" y2="5" stroke="#dc2626" strokeWidth="2.5" strokeDasharray="4,4" />
                  </svg>
                  Protección
                </div>
                <p className="text-[10px] text-slate-600 mt-1 leading-tight">
                  Borde rojo punteado — condición patrimonial/histórica/paisajística <strong>a verificar</strong> en la ordenanza.
                </p>
              </div>
            )}
          </div>
          )}

          <MapContainer center={center} zoom={16} style={{ height: "100%", width: "100%" }} data-testid="lote-map">
            <TileLayer
              key={tileMode}
              attribution={tileMode === "sat" ? TILE_SAT_ATTR : TILE_STREETS_ATTR}
              url={tileMode === "sat" ? TILE_SAT : TILE_STREETS}
            />
            {/* WMS overlays IDECOR (Hidrografía y Riesgo). Se pintan DESPUÉS
                del basemap y ANTES de los polígonos interactivos → quedan
                debajo de lotes/rural sin bloquear click. Opacidad ~0.55 para
                que se vea el terreno. Cada capa es un GetMap independiente
                al mismo endpoint; el group "riesgo" une varias capas
                municipales en una sola request via layers coma-separado. */}
            {wmsCursosAgua && (
              <WMSTileLayer
                url={IDECOR_WMS_URL}
                layers={WMS_LAYER_CURSOS_AGUA}
                format="image/png"
                transparent={true}
                version="1.1.0"
                opacity={0.7}
                attribution={IDECOR_WMS_ATTR}
              />
            )}
            {wmsCuencas && (
              <WMSTileLayer
                url={IDECOR_WMS_URL}
                layers={WMS_LAYER_CUENCAS}
                format="image/png"
                transparent={true}
                version="1.1.0"
                opacity={0.45}
                attribution={IDECOR_WMS_ATTR}
              />
            )}
            {wmsRiesgoInun && (
              <WMSTileLayer
                url={IDECOR_WMS_URL}
                layers={WMS_LAYERS_RIESGO_INUN}
                format="image/png"
                transparent={true}
                version="1.1.0"
                opacity={0.55}
                attribution={IDECOR_WMS_ATTR}
              />
            )}
            {/* Polígono de la zona activa — amarillo brillante + borde grueso.
                Elegido para contrastar contra el mapa satélite (verde-marrón),
                donde teal oscuro se pierde. Sin fill (fillOpacity: 0) para que
                se vea SOLO el borde, sin oscurecer el terreno. */}
            {zonaPositions.length > 0 && (
              <Polygon
                key={`zona-${zonaActiva?.zona_id}`}
                positions={zonaPositions}
                pathOptions={{
                  color: "#facc15",         // amarillo brillante
                  weight: 3,
                  opacity: 0.95,
                  fillOpacity: 0,           // sin relleno
                  dashArray: "8 6",
                  interactive: false,
                }}
                data-testid="zona-polygon"
              />
            )}
            <FitToBounds
              polygons={polygons}
              zonaPolygon={zonaActiva?.poligono}
              flyKey={flownFor}
              onFlown={() => setFlownFor("__done__")}
            />
            {/* Vuela al cambiar de zona en el selector — ver RecenterOnZone.
                `key={zonaId}` remonta el helper por cambio de zona: cero
                closures viejas del render anterior (bug 2026-02-17 "una
                zona atrás"). */}
            <RecenterOnZone key={zonaActiva?.zona_id || "__none__"} zona={zonaActiva} />
            {polygonsVisibles.map(({ lote, positions, protegido }) => (
              <Polygon
                key={lote.lote_id}
                positions={positions}
                pathOptions={
                  protegido ? {
                    // Protección/condición especial — borde rojo punteado
                    // + fill translúcido. Fase 4a (2026-07-24). El punteado
                    // lo diferencia del rojo sólido de `es_ruido` (que hoy
                    // es 0 tras el fix de Fase 1, pero convive semánticamente).
                    color: "#dc2626", weight: 2.5, fillOpacity: 0.18,
                    fillColor: "#dc2626", dashArray: "4,4",
                  } : {
                    color: lote.es_ruido ? "#dc2626" : lote.es_candidato_urbanizacion ? "#059669" : "#0f766e",
                    weight: 2, fillOpacity: 0.35,
                  }
                }
                eventHandlers={{ click: () => setSelectedId(lote.lote_id) }}
              >
                <Popup>
                  <div className="text-sm">
                    <strong>{lote.desig_oficial || lote.nomenclatura}</strong><br/>
                    <span className="font-mono">{Math.round(lote.superficie_m2).toLocaleString("es-AR")} m²</span><br/>
                    <button onClick={() => setSelectedId(lote.lote_id)} className="text-emerald-700 underline mt-1">
                      Ver ficha completa →
                    </button>
                  </div>
                </Popup>
              </Polygon>
            ))}

            {/* Capa Rural — parcelas de campo IDECOR (verde tierra oliva).
                Solo se pinta cuando `capaRural` está prendido; se dibuja
                encima de los baldíos urbanos para que los rurales queden
                visibles si superponen (raro pero posible en bordes de zona). */}
            {capaRural && ruralesFiltrados.map((r) => {
              const positions = toLeafletCoords(r.geometry);
              if (!positions.length) return null;
              return (
                <Polygon
                  key={r.rural_id}
                  positions={positions}
                  pathOptions={{ color: "#65a30d", weight: 2, fillOpacity: 0.30, fillColor: "#84cc16" }}
                  eventHandlers={{ click: () => setRuralSel(r) }}
                />
              );
            })}

            {/* Capa Publicado (2026-07-24) — pines naranjas para terrenos en
                venta. Se pintan al final para que queden sobre lotes/rural
                (son puntos, no polígonos → tapar no es un problema). Click
                abre la ficha del aviso en el panel lateral. */}
            {capaPublicado && publicados.map((av) => {
              if (av.lat == null || av.lng == null) return null;
              return (
                <Marker
                  key={av.opportunity_id}
                  position={[av.lat, av.lng]}
                  icon={PIN_PUBLICADO}
                  eventHandlers={{ click: () => setAvisoSel(av) }}
                />
              );
            })}

            {/* Capa Loteos aprobados (2026-07-25) — polígonos violetas
                translúcidos. Se pintan ANTES que el catastro urbano para
                que los baldíos (foco de Tierra) queden encima y sean
                clickeables. Click abre ficha del loteo. */}
            {capaLoteos && loteos.map((lo) => {
              const positions = toLeafletCoords(lo.geometry);
              if (!positions.length) return null;
              return (
                <Polygon
                  key={lo.loteo_id}
                  positions={positions}
                  pathOptions={{ color: "#9333ea", weight: 2, fillOpacity: 0.20, fillColor: "#a855f7" }}
                  eventHandlers={{ click: () => setLoteoSel(lo) }}
                />
              );
            })}

          </MapContainer>
        </div>
      </div>

      <LoteFicha
        lote={selected}
        onClose={() => setSelectedId(null)}
        onPublicado={(l) => setPublicadoLote(l)}
      />

      {/* Ficha rural — tapa la urbana cuando el user clickea una parcela
          rural. Fase Rural 2026-07-24. Simpler que la urbana: sin FOS/FOT/
          normativa (no aplica), pero con ha, valuación oficial, dueño Rentas. */}
      {ruralSel && <LoteRuralFicha rural={ruralSel} onClose={() => setRuralSel(null)} />}

      {/* Ficha Loteo aprobado (Fase 2026-07-25) — nombre + titular +
          tipo + cantidad de lotes/viviendas + expediente. Contexto de
          mercado: quién está desarrollando qué en la zona. */}
      {loteoSel && <LoteoFicha loteo={loteoSel} onClose={() => setLoteoSel(null)} />}

      {/* Ficha Publicado (aviso de terreno en venta). Fase 2026-07-24.
          Modo A del suelo: precio + $/m² + contacto + link. Cuando el
          aviso tiene `lote_asignado` → botón "Ver lote catastral" que
          abre la ficha urbana del lote (conexión Modo A ↔ catastro). */}
      {avisoSel && (
        <AvisoFicha
          aviso={avisoSel}
          onClose={() => setAvisoSel(null)}
          onVerLote={(loteId) => { setAvisoSel(null); setSelectedId(loteId); }}
        />
      )}

      <ZonaDialog
        open={showZonaDialog}
        onClose={() => { setShowZonaDialog(false); setEditingZona(null); }}
        onSaved={handleZonaSaved}
        zona={editingZona}
        context="tierra"
      />

      {publicadoLote && (
        <PublicadoDialog
          lote={publicadoLote}
          onClose={() => setPublicadoLote(null)}
        />
      )}

      {/* Preview de costo antes de disparar Apify (Fase Publicado 2026-07-24).
          Reusa la misma UX que RadarMapa: tope honesto, no promesa.
          Cancelable durante la corrida (el actor cobra solo por lo devuelto
          hasta el corte). */}
      <AlertDialog open={showCostPreview} onOpenChange={(v) => !v && setShowCostPreview(false)}>
        <AlertDialogContent className="rounded-none z-[1300]" data-testid="capture-cost-preview">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Traer terrenos publicados de «{zonas.find((z) => z.zona_id === zonaId)?.nombre || "esta zona"}» — ¿confirmás?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-xs text-slate-700 space-y-2 pt-1">
                <p>
                  Va a consultar Properati (Zonaprop apagado). Cuesta{" "}
                  <strong>hasta ~US$ {capturaCostPreview.total.toFixed(2)}</strong> según cuántos avisos encuentre.
                  Es un tope, no un número prometido — el real puede ser mucho menor.
                </p>
                <ul className="border-l-2 border-slate-300 pl-3 space-y-0.5 text-[11px] font-mono">
                  {capturaCostPreview.runs.map((r) => (
                    <li key={r.name}>
                      {r.name}: hasta {r.maxItems} avisos × US$ {r.perItem} = US$ {(r.maxItems * r.perItem).toFixed(2)}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-slate-500">
                  Scope: <strong>solo terrenos en venta</strong> (URL <code className="bg-slate-100 px-1">terrenos-venta-…</code>). Podés cancelar durante la corrida.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-300">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="capture-cost-confirm"
              onClick={() => { setShowCostPreview(false); capturarTerrenos(); }}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-none"
            >
              Buscar (hasta ~US$ {capturaCostPreview.total.toFixed(2)})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// GeoJSON [lng,lat] → Leaflet [lat,lng] positions
function toLeafletCoords(geom) {
  if (!geom || !geom.coordinates) return [];
  if (geom.type === "Polygon") {
    return geom.coordinates.map(ring => ring.map(([lng, lat]) => [lat, lng]));
  }
  if (geom.type === "MultiPolygon") {
    return geom.coordinates.map(poly => poly[0].map(([lng, lat]) => [lat, lng]));
  }
  return [];
}

// ────────────────────────────────────────────────────────────────
// Ficha Rural (Fase 2026-07-24) — vista aparte de la urbana
// ────────────────────────────────────────────────────────────────
function LoteRuralFicha({ rural, onClose }) {
  if (!rural) return null;
  const c = rural.centroide || [null, null];
  const lat = c[1], lng = c[0];
  const gmaps = (lat && lng) ? `https://www.google.com/maps?q=${lat},${lng}` : null;
  const fmt = (n) => n == null ? "s/d" : Math.round(n).toLocaleString("es-AR");
  const fmtHa = (ha) => ha == null ? "s/d" : Number(ha).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  return (
    <aside
      className="fixed right-0 top-0 h-full w-full sm:w-[440px] bg-white shadow-2xl overflow-y-auto z-30"
      data-testid="lote-rural-ficha"
    >
      <div className="px-6 py-4 bg-lime-800 text-white flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-lime-200">Parcela rural · IDECOR</p>
          <h2 className="font-heading text-xl font-bold leading-tight mt-1">{rural.desig_oficial || rural.nomenclatura || "Parcela rural"}</h2>
          <p className="text-xs text-lime-100 mt-0.5 font-mono">{rural.nomenclatura}</p>
        </div>
        <button onClick={onClose} data-testid="btn-cerrar-rural"
                className="text-lime-200 hover:text-white text-2xl leading-none">×</button>
      </div>

      <div className="px-6 py-5 space-y-5">
        <section>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Superficie</p>
          <p className="font-mono font-bold text-3xl text-lime-800" data-testid="rural-ha">{fmtHa(rural.superficie_ha)} ha</p>
          <p className="text-xs text-slate-500 mt-0.5">{fmt(rural.superficie_m2)} m²</p>
        </section>

        <section className="border-t border-slate-200 pt-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Valuación rural oficial</p>
          <p className="font-mono font-bold text-xl text-slate-900" data-testid="rural-val">AR$ {fmt(rural.valuacion_ars)}</p>
          {rural.valuacion_ars_ha != null && (
            <p className="text-xs text-slate-500 mt-0.5 font-mono">AR$ {fmt(rural.valuacion_ars_ha)} / ha</p>
          )}
          <p className="text-[10px] text-slate-400 mt-1 italic">Valuación fiscal oficial IDECOR — no es precio de mercado.</p>
        </section>

        <section className="border-t border-slate-200 pt-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2">Ubicación</p>
          <dl className="text-xs space-y-1">
            <div className="flex justify-between"><dt className="text-slate-500">Departamento</dt><dd>{rural.departamento || "s/d"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Pedanía</dt><dd>{rural.pedania || "s/d"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Localidad</dt><dd>{rural.localidad || "s/d"}</dd></div>
            <div className="flex justify-between items-center gap-2">
              <dt className="text-slate-500">Nº de cuenta</dt>
              {rural.nro_cuenta ? (
                <dd className="font-mono flex items-center gap-1.5">
                  <span>{rural.nro_cuenta}</span>
                  <button type="button"
                          onClick={() => navigator.clipboard.writeText(rural.nro_cuenta).then(
                            () => toast.success("Nº de cuenta copiado"),
                            () => toast.error("No pude copiar")
                          )}
                          data-testid="btn-copiar-cuenta-rural"
                          className="text-slate-400 hover:text-lime-700"
                          title="Copiar">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </dd>
              ) : <dd className="text-slate-400">s/d</dd>}
            </div>
          </dl>
        </section>

        <section className="border-t border-slate-200 pt-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600 mb-2">Consultar en fuente oficial</p>
          <div className="space-y-2">
            <a href="https://www.catastrocordoba.gob.ar/ca/chequea-los-datos-de-tu-inmueble/"
               target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"
               data-testid="btn-consulta-catastro-rural"
               className="w-full border-2 border-slate-700 text-slate-900 hover:bg-slate-900 hover:text-white px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 text-left normal-case font-bold">Catastro <span className="text-slate-500 font-normal">(datos parcelarios)</span></span>
            </a>
            <a href="https://www.rentascordoba.gob.ar/cms/informe-del-inmueble-gc/"
               target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"
               data-testid="btn-consulta-rentas-rural"
               className="w-full border-2 border-slate-700 text-slate-900 hover:bg-slate-900 hover:text-white px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 text-left normal-case font-bold">Rentas <span className="text-slate-500 font-normal">(informe · pagos y deuda)</span></span>
            </a>
            {gmaps && (
              <a href={gmaps} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"
                 data-testid="btn-gmaps-rural"
                 className="w-full border-2 border-lime-700 bg-lime-700 text-white hover:bg-lime-800 px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Google Maps
              </a>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Sin costo — links a los sitios oficiales. Pegá el Nº de cuenta.</p>
        </section>
      </div>
    </aside>
  );
}


// ─────────────────────────────────────────────────────────────
// AvisoFicha — panel lateral para avisos de terrenos EN VENTA
// (Fase Publicado 2026-07-24). Modo A del suelo: mostrar precio, $/m²,
// superficie, fuente (Properati/Zonaprop), contacto (vendedor) y link
// a la publicación. Si el aviso ya está cruzado con un lote del
// catastro (`lote_asignado`), botón "Ver lote catastral" → abre la
// ficha urbana. Regla de Honestidad: campos faltantes muestran "S/D",
// no se inventa nada.
// ─────────────────────────────────────────────────────────────
function AvisoFicha({ aviso, onClose, onVerLote }) {
  if (!aviso) return null;
  const precio = fmtMoney(aviso.precio, aviso.moneda);
  const supNum = Number(aviso.superficie_m2 || 0);
  const supTxt = supNum > 0 ? `${supNum.toLocaleString("es-AR")} m²` : "S/D";
  const pM2Num = (aviso.precio && supNum > 0) ? Math.round(aviso.precio / supNum) : null;
  const pM2Txt = pM2Num != null ? `${aviso.moneda || ""} ${pM2Num.toLocaleString("es-AR")} / m²`.trim() : "S/D";
  const fuente = aviso.fuente ? aviso.fuente.charAt(0).toUpperCase() + aviso.fuente.slice(1) : "S/D";
  const vendedor = (aviso.vendedor && String(aviso.vendedor).trim()) || null;
  const tipoVendLabel = aviso.tipo_vendedor === "dueno_directo" ? "Dueño directo"
                      : aviso.tipo_vendedor === "inmobiliaria" ? "Inmobiliaria"
                      : null;
  const gmaps = (aviso.lat != null && aviso.lng != null) ? `https://www.google.com/maps?q=${aviso.lat},${aviso.lng}` : null;

  return (
    <aside
      className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl overflow-y-auto z-30"
      data-testid="aviso-ficha"
    >
      <div className="px-6 py-4 bg-orange-600 text-white flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-orange-100 flex items-center gap-1.5">
            <Tag className="w-3 h-3" /> Terreno publicado · {fuente}
          </p>
          <h2 className="font-heading text-lg font-bold leading-tight mt-1 line-clamp-2" data-testid="aviso-titulo">
            {aviso.titulo || "Terreno en venta"}
          </h2>
        </div>
        <button onClick={onClose} data-testid="btn-cerrar-aviso"
                className="text-orange-100 hover:text-white shrink-0" aria-label="Cerrar">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-6 py-5 space-y-5">
        <section>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Precio publicado</p>
          <p className="font-mono font-bold text-3xl text-orange-700" data-testid="aviso-precio">{precio}</p>
          <p className="text-xs text-slate-500 mt-0.5 font-mono" data-testid="aviso-precio-m2">{pM2Txt}</p>
        </section>

        <section className="border-t border-slate-200 pt-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Superficie</p>
          <p className="font-mono font-bold text-xl text-slate-900" data-testid="aviso-superficie">{supTxt}</p>
          {supNum === 0 && (
            <p className="text-[10px] text-slate-400 mt-0.5 italic">
              El aviso no informa superficie — verificar en la publicación.
            </p>
          )}
        </section>

        <section className="border-t border-slate-200 pt-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2">Contacto</p>
          <div className="text-xs space-y-1">
            <div className="flex items-start gap-2">
              <User className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                {vendedor ? (
                  <span data-testid="aviso-vendedor">{vendedor}</span>
                ) : (
                  <span className="text-slate-400 italic" data-testid="aviso-vendedor-sd">Sin datos del vendedor en el aviso</span>
                )}
                {tipoVendLabel && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5">
                    {tipoVendLabel}
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 pt-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600 mb-2">Enlaces</p>
          <div className="space-y-2">
            {aviso.url && (
              <a href={aviso.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"
                 data-testid="btn-abrir-publicacion"
                 className="w-full border-2 border-orange-700 bg-orange-600 text-white hover:bg-orange-700 px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
                <LinkIcon className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 text-left">Abrir publicación</span>
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              </a>
            )}
            {aviso.lote_asignado && (
              <button type="button"
                      onClick={() => onVerLote && onVerLote(aviso.lote_asignado)}
                      data-testid="btn-ver-lote-catastral"
                      className="w-full border-2 border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 text-left normal-case font-bold">Ver lote catastral <span className="text-slate-500 font-normal">(datos oficiales)</span></span>
              </button>
            )}
            {gmaps && (
              <a href={gmaps} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"
                 data-testid="btn-aviso-gmaps"
                 className="w-full border-2 border-slate-700 text-slate-900 hover:bg-slate-900 hover:text-white px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Ver en Google Maps
              </a>
            )}
          </div>
          {/* Honestidad 2026-07-26 — la coordenada del pin viene de la
              dirección del aviso (portal), no de la parcela catastral.
              Se ubica JUNTO al botón Google Maps (donde el usuario usa la
              ubicación) para que la aclaración cambie una decisión concreta:
              no cerrar un negocio asumiendo que el pin cae sobre el lote. */}
          <p className="text-[10px] text-slate-500 mt-3 italic leading-tight" data-testid="aviso-ubicacion-aproximada">
            <span className="not-italic">📍</span> <strong className="text-slate-700 not-italic">Ubicación aproximada.</strong> Viene de la dirección del aviso (portal), no de la parcela catastral — puede caer sobre un lote vecino o la calle.
          </p>
          <p className="text-[10px] text-slate-500 mt-2 italic">
            Fuente: {fuente}. Los datos publicados son responsabilidad del anunciante.
          </p>
        </section>
      </div>
    </aside>
  );
}



// ─────────────────────────────────────────────────────────────
// LoteoFicha — panel lateral para loteos aprobados/autorizados
// (Fase 2026-07-25). Contexto de mercado: quién ya desarrolló qué en
// la zona. Fuente: IDECOR (municipios). Regla de Honestidad: campos
// faltantes muestran "S/D", cobertura solo Córdoba capital.
// ─────────────────────────────────────────────────────────────
function LoteoFicha({ loteo, onClose }) {
  if (!loteo) return null;
  const nombre = loteo.nombre || loteo.nombfant || "Loteo sin nombre";
  const isAprobado = loteo.source_layer === "loteos_muni_aprobados";
  const layerLabel = isAprobado ? "Aprobado" : loteo.source_layer === "loteos_muni_autorizados" ? "Autorizado" : "S/D";
  const sd = (v) => (v == null || v === "" ? "S/D" : String(v));
  const fmt = (n) => n == null ? "S/D" : Number(n).toLocaleString("es-AR");

  // Pulir el texto del trámite (Fase 2026-07-26): el valor de IDECOR viene
  // como "RESOLUCION 0399-29/11/17" — la etiqueta ya dice "Resolución", así
  // que sacamos la palabra inicial para que no quede repetida.
  const stripPrefix = (raw) => {
    if (!raw) return null;
    const cleaned = String(raw).replace(
      /^\s*(resoluci[oó]n(es)?|resol\.?|decreto|dec\.?)\s+/i,
      "",
    ).trim();
    return cleaned || String(raw);
  };
  // Parsea la primera fecha `DD/MM/AA(AA)` y la formatea a "mes año" en es-AR.
  // Si no matchea, devuelve null → el caller no muestra el chip (regla de
  // honestidad: nada de texto roto).
  const extractFecha = (raw) => {
    if (!raw) return null;
    const m = String(raw).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (!m) return null;
    const [, , mm, yyRaw] = m;
    const mmNum = parseInt(mm, 10);
    if (mmNum < 1 || mmNum > 12) return null;
    let year = parseInt(yyRaw, 10);
    if (yyRaw.length === 2) year = year < 70 ? 2000 + year : 1900 + year;
    const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
    return `${meses[mmNum - 1]} ${year}`;
  };

  const tramiteRaw = isAprobado ? loteo.decresapro : loteo.autorizado;
  const tramiteClean = stripPrefix(tramiteRaw);
  const tramiteFecha = extractFecha(tramiteRaw);
  const tramiteLabel = isAprobado ? "Decreto" : "Resolución";
  const chipLabel = isAprobado ? "Aprobado" : "Autorizado";

  return (
    <aside
      className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl overflow-y-auto z-30"
      data-testid="loteo-ficha"
    >
      <div className="px-6 py-4 bg-purple-600 text-white flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-purple-100">
            Loteo · {layerLabel}
          </p>
          <h2 className="font-heading text-lg font-bold leading-tight mt-1 line-clamp-2" data-testid="loteo-nombre">
            {nombre}
          </h2>
        </div>
        <button onClick={onClose} data-testid="btn-cerrar-loteo"
                className="text-purple-100 hover:text-white shrink-0" aria-label="Cerrar">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-6 py-5 space-y-5">
        <section>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Desarrollador (Titular)</p>
          <p className="font-mono font-bold text-base text-slate-900 leading-tight" data-testid="loteo-titular">
            {sd(loteo.titular)}
          </p>
        </section>

        <section className="border-t border-slate-200 pt-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2">Tipo</p>
          <p className="text-sm text-slate-900" data-testid="loteo-tipo">{sd(loteo.tipoloteo)}</p>
        </section>

        <section className="border-t border-slate-200 pt-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2">Escala</p>
          <dl className="text-sm space-y-1">
            <div className="flex justify-between">
              <dt className="text-slate-500">Cantidad de lotes</dt>
              <dd className="font-mono font-bold" data-testid="loteo-cantlotes">{fmt(loteo.cantlotes)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Cantidad de viviendas</dt>
              <dd className="font-mono">{fmt(loteo.cantviv)}</dd>
            </div>
          </dl>
        </section>

        <section className="border-t border-slate-200 pt-4">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2">Trámite</p>
          <dl className="text-xs space-y-1">
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Expediente</dt>
              <dd className="font-mono text-right">{sd(loteo.expediente_1)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Nomenclatura</dt>
              <dd className="font-mono text-right">{sd(loteo.nomenclatu)}</dd>
            </div>
            {/* Trámite oficial: muestro decreto si el loteo está aprobado,
                resolución de autorización si está autorizado. Fix 2026-07-26
                (Gonzalo): antes mostraba siempre `decresapro`, dejando en
                "S/D" a los autorizados que sí traen `autorizado`. Pulido
                2026-07-26 (Gonzalo v2): saco la palabra repetida
                ("Resolución RESOLUCION 0399…" → "Resolución 0399…") y
                sumo chip con la fecha parseada. */}
            {isAprobado ? (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{tramiteLabel}</dt>
                <dd className="font-mono text-right" data-testid="loteo-decreto">{tramiteClean == null ? "S/D" : tramiteClean}</dd>
              </div>
            ) : (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">{tramiteLabel}</dt>
                <dd className="font-mono text-right" data-testid="loteo-resolucion">{tramiteClean == null ? "S/D" : tramiteClean}</dd>
              </div>
            )}
            {tramiteFecha && (
              <div className="flex justify-end pt-1">
                <span
                  className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-800"
                  data-testid="loteo-tramite-fecha"
                >
                  {chipLabel}: {tramiteFecha}
                </span>
              </div>
            )}
          </dl>
          {loteo.documento_url && (
            <a href={loteo.documento_url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer"
               data-testid="loteo-documento-oficial"
               className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-purple-700 hover:text-purple-900 border-b border-purple-700 hover:border-purple-900">
              <ExternalLink className="w-3 h-3" />
              Ver documento oficial
            </a>
          )}
        </section>

        <p className="text-[10px] text-slate-400 italic border-t border-slate-100 pt-3">
          Fuente: IDECOR (Municipalidad de Córdoba). Cobertura: solo capital.
        </p>
      </div>
    </aside>
  );
}
